import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, Lock, AlertCircle, User, LogOut, ArrowRight } from 'lucide-react';
import { Logo } from '../components/Logo';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import { NafathLoginButton } from '../components/NafathLogin';
import Turnstile from '../components/Turnstile';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const { t, isRTL, dir } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const checkSession = () => {
      try {
        const userStr = localStorage.getItem('user');
        const token = (document.cookie.split('; ').find(row => row.startsWith('csrf_token='))?.split('=')[1] || '');
        // console.log("DEBUG Login checkSession: userStr =", userStr, "token =", token, "document.cookie =", document.cookie);
        if (userStr && token) {
          const userData = JSON.parse(userStr);
          setCurrentUser(userData);
        }
      } catch (e) {
        // Ignore
      }
    };
    checkSession();
  }, []);

  const handleLogout = async () => {
    setLoading(true);
    try {
      const token = (document.cookie.split('; ').find(row => row.startsWith('csrf_token='))?.split('=')[1] || '');
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { 'X-CSRF-Token': token },
        withCredentials: true
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      setCurrentUser(null);
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!turnstileToken) {
      setError(isRTL ? "يرجى التحقق من اختبار الكابتشا" : "Please verify the captcha challenge");
      return;
    }
    setError('');
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);

      const response = await axios.post(`${API}/auth/login`, formData, {
        headers: {
          'X-Turnstile-Token': turnstileToken
        }
      });
      
      // console.log("DEBUG Login handleLogin response:", response.data, "cookies =", document.cookie);
      if (response.data && response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        if (response.data.csrf_token) {
          localStorage.setItem('token', response.data.csrf_token);
        }
        setCurrentUser(response.data.user);
      }

      // تأخير صغير للتأكد من حفظ البيانات
      setTimeout(() => {
        navigate('/dashboard');
      }, 100);
    } catch (err) {
      console.error('Login Error:', err);
      setError(t('msg.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  if (currentUser) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center relative overflow-hidden" dir={dir}>
        <div className="fixed top-4 left-4 z-50">
          <LanguageSelector />
        </div>
        
        <div className="w-full max-w-md px-6 relative z-10">
          <div className="text-center mb-8">
            <Logo className="mx-auto h-16 w-auto" />
          </div>
          
          <div className="bg-white dark:bg-[#004225] rounded-2xl shadow-xl border border-gray-100 p-8 text-center relative overflow-hidden">
            {/* Top gold badge */}
            <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-gold via-yellow-500 to-gold" />
            
            <div className="mx-auto w-16 h-16 bg-green-50 dark:bg-[#d4af37] rounded-full flex items-center justify-center mb-6">
              <User className="w-8 h-8 text-[#004225]" />
            </div>
            
            <h2 className="text-2xl font-bold text-primary mb-2 font-heading">
              {isRTL ? "أنت مسجل بالفعل" : "You are already logged in"}
            </h2>
            <p className="text-sm text-gray-500 mb-6 font-body">
              {isRTL ? "مسجل بالمعلومات التالية:" : "Logged in with the following account:"}
            </p>
            
            <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-100 text-right font-body">
              <div className="mb-2">
                <span className="text-xs text-gray-400 block">{isRTL ? "الاسم الكامل" : "Full Name"}</span>
                <span className="text-sm font-semibold text-gray-800">{currentUser.full_name || currentUser.email}</span>
              </div>
              <div>
                <span className="text-xs text-gray-400 block">{isRTL ? "البريد الإلكتروني" : "Email Address"}</span>
                <span className="text-sm font-medium text-gray-600 break-all">{currentUser.email}</span>
              </div>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-[#004225] dark:bg-[#062718] text-white py-3 rounded-md flex items-center justify-center gap-2 font-heading font-medium"
              >
                {isRTL ? "متابعة بهذا الحساب" : "Continue with this account"}
                <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
              </button>
              
              <button
                onClick={handleLogout}
                disabled={loading}
                className="w-full bg-white dark:bg-[#062718] border border-gray-200 text-red-600 py-3 rounded-md hover:bg-red-50 hover:border-red-100 transition-all duration-300 flex items-center justify-center gap-2 font-heading font-medium"
              >
                <LogOut className="w-5 h-5" />
                {isRTL ? "تسجيل خروج" : "Logout"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand flex items-center justify-center relative overflow-hidden" dir={dir}>
      {/* Language Selector - Fixed Position */}
      <div className="fixed top-4 left-4 z-50">
        <LanguageSelector />
      </div>
      
      <div className="w-full max-w-md px-6 relative z-10">
        {/* Top ornamental crest */}
        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-3" aria-hidden="true">
            <span className="w-16 h-px bg-gradient-to-l from-transparent to-gold" />
            <svg width="28" height="14" viewBox="0 0 28 14" className="text-gold">
              <path d="M14 1 L19 7 L14 13 L9 7 Z" fill="currentColor" fillOpacity="0.5" stroke="currentColor" />
            </svg>
            <span className="w-16 h-px bg-gradient-to-r from-transparent to-gold" />
          </div>
        </div>

        <div className="relative bg-white/95 dark:bg-[#004225] backdrop-blur-sm rounded-2xl shadow-2xl p-8 islamic-card">
          {/* corner ornaments */}
          <svg className="absolute top-2 left-2 w-8 h-8 text-gold pointer-events-none" viewBox="0 0 32 32" aria-hidden="true">
            <g fill="none" stroke="currentColor" strokeWidth="1">
              <path d="M2 2 L26 2 M2 2 L2 26" />
              <path d="M2 2 Q14 4 18 16 Q22 26 14 28" />
              <circle cx="22" cy="22" r="2" />
            </g>
          </svg>
          <svg className="absolute top-2 right-2 w-8 h-8 text-gold pointer-events-none" viewBox="0 0 32 32" aria-hidden="true">
            <g fill="none" stroke="currentColor" strokeWidth="1" transform="scale(-1,1) translate(-32,0)">
              <path d="M2 2 L26 2 M2 2 L2 26" />
              <path d="M2 2 Q14 4 18 16 Q22 26 14 28" />
              <circle cx="22" cy="22" r="2" />
            </g>
          </svg>
          <svg className="absolute bottom-2 left-2 w-8 h-8 text-gold pointer-events-none" viewBox="0 0 32 32" aria-hidden="true">
            <g fill="none" stroke="currentColor" strokeWidth="1" transform="scale(1,-1) translate(0,-32)">
              <path d="M2 2 L26 2 M2 2 L2 26" />
              <path d="M2 2 Q14 4 18 16 Q22 26 14 28" />
            </g>
          </svg>
          <svg className="absolute bottom-2 right-2 w-8 h-8 text-gold pointer-events-none" viewBox="0 0 32 32" aria-hidden="true">
            <g fill="none" stroke="currentColor" strokeWidth="1" transform="scale(-1,-1) translate(-32,-32)">
              <path d="M2 2 L26 2 M2 2 L2 26" />
              <path d="M2 2 Q14 4 18 16 Q22 26 14 28" />
            </g>
          </svg>

          <div className="flex flex-col items-center justify-center gap-3 mb-6 relative">
            <div className="p-2 rounded-xl bg-primary border-2 border-gold/60 shadow-lg">
              <Logo size="lg" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-[#004225] text-center">
              {t('login.title')}
            </h1>
            <div className="flex items-center gap-2 w-full" aria-hidden="true">
              <span className="flex-1 h-px bg-gradient-to-l from-transparent via-gold/60 to-gold" />
              <svg width="22" height="10" viewBox="0 0 22 10" className="text-gold">
                <path d="M11 1 L15 5 L11 9 L7 5 Z" fill="currentColor" fillOpacity="0.4" stroke="currentColor" strokeWidth="0.8" />
              </svg>
              <span className="flex-1 h-px bg-gradient-to-r from-transparent via-gold/60 to-gold" />
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-center gap-3" data-testid="login-error">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-sm text-red-700 font-body">{error}</p>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className={`block text-sm font-medium text-[#004225] mb-2 ${isRTL ? 'text-right' : 'text-left'} font-body`}>
                {t('login.email')}
              </label>
              <div className="relative">
                <Mail className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400`} />
                <input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body`}
                  placeholder="example@email.com"
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium text-[#004225] mb-2 ${isRTL ? 'text-right' : 'text-left'} font-body`}>
                {t('login.password')}
              </label>
              <div className="relative">
                <Lock className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400`} />
                <input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body`}
                  placeholder="••••••••"
                />
              </div>
            </div>

            {/* Cloudflare Turnstile */}
            <div className="my-4">
              <Turnstile 
                sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                onVerify={setTurnstileToken} 
              />
            </div>

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-amber-600 text-white py-3 rounded-md transition-all duration-300 disabled:opacity-50 font-heading font-medium"
            >
              {loading ? t('msg.loading') : t('login.loginBtn')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 font-body">
              {t('login.noAccount')}{' '}
              <button
                data-testid="login-to-register-link"
                onClick={() => navigate('/register')}
                className="text-gold hover:text-gold-dark font-semibold"
              >
                {t('login.createAccount')}
              </button>
            </p>
          </div>

          {/* فاصل */}
          {/* <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">{isRTL ? 'أو' : 'OR'}</span>
            </div>
          </div> */}

          {/* زر نفاذ */}
          {/* <NafathLoginButton
            className="w-full"
            onSuccess={(user) => {
              navigate('/dashboard');
            }}
            onError={(msg) => setError(msg)}
          /> */}

          <div className="mt-6 text-center">
            <button
              data-testid="login-back-home-btn"
              onClick={() => navigate('/')}
              className="text-sm text-gray-500 hover:text-primary font-body"
            >
              {t('nav.home')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
